import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../utils/api';
import { getCurrentUser, isAllBranchesRole } from '../utils/auth';
import { useToast } from './ToastContext';
import { useAutoSync } from '../hooks/useAutoSync';

const ScrollHintIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 5l7 7m0 0l-7 7m7-7H3" />
  </svg>
);

function CollectionEntryList({ companyId }) {
  const currentUser = getCurrentUser();
  const canSelectAllBranches = isAllBranchesRole(currentUser?.role);

  const location = useLocation();
  const [agedData, setAgedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [agingAsOfDate, setAgingAsOfDate] = useState(new Date().toISOString().split('T')[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage] = useState(15);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [recordsCached, setRecordsCached] = useState(0);
  
  // Toast notifications
  const { showToast } = useToast();
  
  // Auto-sync monitoring
  useAutoSync(true);

  // Branch filter
  const ALL_BRANCHES_VALUE = '';
  const [selectedBranch, setSelectedBranch] = useState(companyId ? String(companyId) : '');
  const [companies, setCompanies] = useState([]);

  // Check if we're coming from Customers page with a pre-filled search
  useEffect(() => {
    if (location.state?.customerSearch) {
      setSearchTerm(location.state.customerSearch);
      // Clear the state after using it so back button doesn't re-trigger it
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data || []);
    } catch (err) {
      console.error('Failed to fetch companies:', err);
    }
  };

  const getCompanyIdParam = () => {
    if (!canSelectAllBranches && !selectedBranch) return companyId || '';
    return selectedBranch === ALL_BRANCHES_VALUE ? '' : selectedBranch;
  };

  // On mount or when date/branch changes, load data and auto-generate if empty
  useEffect(() => {
    loadAgedARData(true);

    // Auto-refresh every 30 minutes to pick up backend auto-sync updates
    const intervalId = setInterval(() => {
      loadAgedARData(false);
    }, 30 * 60 * 1000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agingAsOfDate, selectedBranch]);

  const loadAgedARData = async (autoGenerateIfEmpty = false) => {
    setLoading(true);
    setMessage('');
    try {
      const companyIdParam = getCompanyIdParam();
      const params = new URLSearchParams();
      params.append('aging_as_of_date', agingAsOfDate);
      if (companyIdParam) params.append('company_id', companyIdParam);

      const response = await api.get(`/reports/aged-ar?${params.toString()}`);
      setAgedData(response.data);
      if (response.data.length > 0) {
        setLastSyncAt(response.data[0].last_sync_at);
        setRecordsCached(response.data.length);
      } else {
        setLastSyncAt(null);
        setRecordsCached(0);
      }
      setCurrentPage(1);

      // Auto-generate if no cached data exists (only on initial load / date change)
      if (response.data.length === 0 && autoGenerateIfEmpty) {
        await generateAgedARForDate(agingAsOfDate);
      }
    } catch (err) {
      setMessage('Failed to load aged AR data.');
    } finally {
      setLoading(false);
    }
  };

  const generateAgedARForDate = async (date) => {
    setSyncing(true);
    setLoading(true);
    setMessage(`Generating aged AR report as of ${new Date(date).toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' })}...`);
    try {
      const companyIdParam = getCompanyIdParam();
      const response = await api.post('/reports/aged-ar/generate', { aging_as_of_date: date, company_id: companyIdParam });
      setLastSyncAt(response.data.last_sync_at);
      setRecordsCached(response.data.records_cached || 0);
      await reloadAgedAR(date);
      setMessage(`Aged AR report generated successfully! ${response.data.records_cached || 0} records cached.`);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to generate aged AR report for selected date.';
      const errorDetail = err.response?.data?.error || '';
      setMessage(`Generate failed: ${errorMessage}${errorDetail ? ` (${errorDetail})` : ''}`);
    } finally {
      setSyncing(false);
      setLoading(false);
    }
  };

  const reloadAgedAR = async (date = null) => {
    try {
      const companyIdParam = getCompanyIdParam();
      const params = new URLSearchParams();
      if (date) params.append('aging_as_of_date', date);
      if (companyIdParam) params.append('company_id', companyIdParam);

      const response = await api.get(`/reports/aged-ar?${params.toString()}`);
      setAgedData(response.data);
      if (response.data.length > 0) {
        setLastSyncAt(response.data[0].last_sync_at);
      }
      setRecordsCached(response.data.length);
      setCurrentPage(1);
    } catch (err) {
      console.error('Failed to reload aged AR data:', err);
    }
  };

  const refreshAgedAR = async () => {
    setSyncing(true);
    setMessage('Generating aged AR report...');
    try {
      const companyIdParam = getCompanyIdParam();
      await api.post('/reports/aged-ar/generate', agingAsOfDate ? { aging_as_of_date: agingAsOfDate, company_id: companyIdParam } : {});
      await reloadAgedAR(agingAsOfDate);
      setMessage('Aged AR report refreshed successfully!');
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to refresh aged AR report.';
      const errorDetail = err.response?.data?.error || '';
      setMessage(`Refresh failed: ${errorMessage}${errorDetail ? ` (${errorDetail})` : ''}`);
    } finally {
      setSyncing(false);
    }
  };

  // Filter data based on search term only
  const getFilteredData = () => {
    return agedData.filter(row => {
      const matchesSearch = !searchTerm ||
        row.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.salesperson_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (row.customer_no && row.customer_no.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (row.customer_code && row.customer_code.toLowerCase().includes(searchTerm.toLowerCase()));

      return matchesSearch;
    });
  };

  const filteredData = getFilteredData();

  // Pagination
  const totalPages = Math.ceil(filteredData.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  // Calculate totals based on filtered data
  const calculateTotals = () => {
    return filteredData.reduce((acc, row) => ({
      balance_amount: acc.balance_amount + parseFloat(row.balance_amount || 0),
      bucket_1_31_days: acc.bucket_1_31_days + parseFloat(row.bucket_1_31_days || 0),
      bucket_32_62_days: acc.bucket_32_62_days + parseFloat(row.bucket_32_62_days || 0),
      bucket_63_92_days: acc.bucket_63_92_days + parseFloat(row.bucket_63_92_days || 0),
      bucket_93_123_days: acc.bucket_93_123_days + parseFloat(row.bucket_93_123_days || 0),
      bucket_after_123_days: acc.bucket_after_123_days + parseFloat(row.bucket_after_123_days || 0),
    }), {
      balance_amount: 0,
      bucket_1_31_days: 0,
      bucket_32_62_days: 0,
      bucket_63_92_days: 0,
      bucket_93_123_days: 0,
      bucket_after_123_days: 0,
    });
  };

  const totals = calculateTotals();

  return (
    <div className="aged-ar-container">
      <div className="page-header">
        <h1>Aged Accounts Receivable</h1>
        <button
          className="btn"
          onClick={refreshAgedAR}
          disabled={syncing}
          style={{ backgroundColor: '#e63740', color: '#ffffff' }}
        >
          {syncing ? 'Generating...' : 'Refresh Aging Data'}
        </button>
      </div>

      {/* Cache Status */}
      {recordsCached > 0 && (
        <div className="cache-status">
          <span>
            <strong>Cache Status:</strong>{' '}
            {recordsCached} records cached
            {lastSyncAt && (
              <> · Last synced: {new Date(lastSyncAt).toLocaleString()}</>
            )}
          </span>
        </div>
      )}

      {message && (
        <div className={`alert ${message.includes('success') ? 'alert-success' : 'alert-error'}`}>
          {message}
        </div>
      )}

      {/* Search and Branch Filter */}
      <div className="card report-filters" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '15px' }}>
        {canSelectAllBranches && (
          <select
            value={selectedBranch}
            onChange={(e) => { setSelectedBranch(e.target.value); setCurrentPage(1); }}
            className="form-control"
            style={{ width: '180px' }}
          >
            <option value={ALL_BRANCHES_VALUE}>All Branches</option>
            {companies.map((company) => (
              <option key={company.id} value={String(company.id)}>
                {company.name}
              </option>
            ))}
          </select>
        )}

        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          className="form-control search-input"
          style={{ width: '140px', maxWidth: '100%', minWidth: '100px' }}
        />
      </div>

      {/* Aging Summary - Integrated with Date Picker */}
      <div className="grand-total-card">
        {agingAsOfDate && (
          <div className="aged-as-of">
            Aged as of: {new Date(agingAsOfDate + 'T00:00:00').toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' })}
            {lastSyncAt && (
              <>
                {' '}| Cached: {new Date(lastSyncAt).toLocaleString('en-PH')}
              </>
            )}
          </div>
        )}

        <div className="grand-total-header">
          <h2>Aging Summary</h2>
          <div className="grand-total-actions">
            <input
              type="date"
              value={agingAsOfDate || ''}
              onChange={(e) => {
                setAgingAsOfDate(e.target.value);
                setCurrentPage(1);
              }}
              className="form-control"
              style={{ width: '160px' }}
            />
            <button
              onClick={() => {
                setSearchTerm('');
                setAgingAsOfDate(new Date().toISOString().split('T')[0]);
                setCurrentPage(1);
              }}
              className="btn btn-sm btn-secondary"
            >
              Clear
            </button>
          </div>
        </div>

        <div className="grand-total-grid">
          <div className="grand-total-item">
            <span className="label">Total Balance</span>
            <span className="value">{formatCurrency(totals.balance_amount)}</span>
          </div>
          <div className="grand-total-item">
            <span className="label">1-31 Days</span>
            <span className="value">{formatCurrency(totals.bucket_1_31_days)}</span>
          </div>
          <div className="grand-total-item">
            <span className="label">32-62 Days</span>
            <span className="value">{formatCurrency(totals.bucket_32_62_days)}</span>
          </div>
          <div className="grand-total-item">
            <span className="label">63-92 Days</span>
            <span className="value">{formatCurrency(totals.bucket_63_92_days)}</span>
          </div>
          <div className="grand-total-item">
            <span className="label">93-123 Days</span>
            <span className="value">{formatCurrency(totals.bucket_93_123_days)}</span>
          </div>
          <div className="grand-total-item highlight">
            <span className="label">After 123 Days</span>
            <span className="value">{formatCurrency(totals.bucket_after_123_days)}</span>
          </div>
        </div>
      </div>

      <div className="card ar-table-card aged-ar-card">
        {loading ? (
          <div className="loading">Loading aged AR data...</div>
        ) : filteredData.length === 0 ? (
          <div className="empty-state">No aged AR data available. Click "Refresh Aging Data" to generate.</div>
        ) : (
          <>
            <div className="table-scroll-hint">
              {ScrollHintIcon}
              <span>Swipe or scroll horizontally to see all columns</span>
            </div>
            <table className="table ar-table table-compact">
              <thead>
                <tr>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Salesperson</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Customer Code</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Customer</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Payment Terms</th>
                  <th className="text-right" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Balance</th>
                  <th className="text-right" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>1-31 Days</th>
                  <th className="text-right" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>32-62 Days</th>
                  <th className="text-right" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>63-92 Days</th>
                  <th className="text-right" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>93-123 Days</th>
                  <th className="text-right" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>After 123 Days</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row) => (
                  <tr key={row.customer_id}>
                    <td>{row.salesperson_name}</td>
                    <td>{row.customer_code || '-'}</td>
                    <td><strong>{row.customer_name}</strong></td>
                    <td>{row.due_date_text || '-'}</td>
                    <td className="text-right balance">{formatCurrency(row.balance_amount)}</td>
                    <td className="text-right">{formatCurrency(row.bucket_1_31_days)}</td>
                    <td className="text-right">{formatCurrency(row.bucket_32_62_days)}</td>
                    <td className="text-right">{formatCurrency(row.bucket_63_92_days)}</td>
                    <td className="text-right">{formatCurrency(row.bucket_93_123_days)}</td>
                    <td className="text-right">{formatCurrency(row.bucket_after_123_days)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan="3"><strong>TOTAL</strong></td>
                  <td className="text-right">-</td>
                  <td className="text-right">{formatCurrency(totals.balance_amount)}</td>
                  <td className="text-right">{formatCurrency(totals.bucket_1_31_days)}</td>
                  <td className="text-right">{formatCurrency(totals.bucket_32_62_days)}</td>
                  <td className="text-right">{formatCurrency(totals.bucket_63_92_days)}</td>
                  <td className="text-right">{formatCurrency(totals.bucket_93_123_days)}</td>
                  <td className="text-right">{formatCurrency(totals.bucket_after_123_days)}</td>
                </tr>
              </tfoot>
            </table>

            {totalPages > 1 && (
              <div className="pagination-controls">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="btn btn-sm btn-primary"
                  style={{ backgroundColor: '#e63740', borderColor: '#e63740' }}
                >
                  Previous
                </button>

                <span className="pagination-info">
                  Page {currentPage} of {totalPages} ({filteredData.length} records)
                </span>

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="btn btn-sm btn-primary"
                  style={{ backgroundColor: '#e63740', borderColor: '#e63740' }}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function formatCurrency(value) {
  if (value === undefined || value === null) return '0.00';
  const num = parseFloat(value);
  if (Number.isNaN(num)) return '0.00';
  
  // Always show with minus sign if negative, no parenthesis, no peso sign
  if (num < 0) {
    return `${num.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  
  return `${Math.abs(num).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default CollectionEntryList;
